import { useEffect, useRef } from "react";
import { Map as MapLibreMap } from "maplibre-gl";
import { bbox } from "@turf/turf";
import type { TaskGeometry } from "@/types/task";
import "maplibre-gl/dist/maplibre-gl.css";

interface GeometryPreviewMapProps {
  geometry?: TaskGeometry;
  height?: string;
}

export const GeometryPreviewMap: React.FC<GeometryPreviewMapProps> = ({
  geometry,
  height = "200px",
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<MapLibreMap | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Initialize map
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new MapLibreMap({
        container: mapRef.current,
        style:
          "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
        center: [-98.5795, 39.8283], // Center of US as default [lng, lat]
        zoom: 10,
        attributionControl: false,
      });
    }

    const map = mapInstanceRef.current;

    // Remove existing geometry layer if it exists
    if (map.getSource("preview-geometry")) {
      if (map.getLayer("preview-geometry-fill")) {
        map.removeLayer("preview-geometry-fill");
      }
      if (map.getLayer("preview-geometry-line")) {
        map.removeLayer("preview-geometry-line");
      }
      if (map.getLayer("preview-geometry-point")) {
        map.removeLayer("preview-geometry-point");
      }
      map.removeSource("preview-geometry");
    }

    // Add geometry if provided
    if (geometry) {
      try {
        const geojson: GeoJSON.FeatureCollection = {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: geometry,
              properties: {},
            },
          ],
        };

        map.once("idle", () => {
          if (!map.getSource("preview-geometry")) {
            map.addSource("preview-geometry", {
              type: "geojson",
              data: geojson,
            });

            // Add layers based on geometry type
            if (
              geometry.type === "Polygon" ||
              geometry.type === "MultiPolygon"
            ) {
              map.addLayer({
                id: "preview-geometry-fill",
                type: "fill",
                source: "preview-geometry",
                paint: {
                  "fill-color": "#374795",
                  "fill-opacity": 0.4,
                },
              });
              map.addLayer({
                id: "preview-geometry-line",
                type: "line",
                source: "preview-geometry",
                paint: {
                  "line-color": "#374795",
                  "line-width": 2,
                  "line-opacity": 1,
                },
              });
            } else if (
              geometry.type === "LineString" ||
              geometry.type === "MultiLineString"
            ) {
              map.addLayer({
                id: "preview-geometry-line",
                type: "line",
                source: "preview-geometry",
                paint: {
                  "line-color": "#374795",
                  "line-width": 4,
                  "line-opacity": 1,
                },
              });
            } else if (
              geometry.type === "Point" ||
              geometry.type === "MultiPoint"
            ) {
              map.addLayer({
                id: "preview-geometry-point",
                type: "circle",
                source: "preview-geometry",
                paint: {
                  "circle-radius": 8,
                  "circle-color": "#2563eb",
                  "circle-stroke-color": "#2563eb",
                  "circle-stroke-width": 3,
                  "circle-opacity": 0.7,
                },
              });
            }

            // Fit map to geometry bounds
            try {
              const [minLng, minLat, maxLng, maxLat] = bbox(geojson);
              map.fitBounds(
                [
                  [minLng, minLat],
                  [maxLng, maxLat],
                ],
                { padding: 20, duration: 0 }
              );
              // @TODO make max for point, and multiple zoom 16
              if (geojson.features[0].geometry.type === "Point") {
                map.setZoom(16.5);
              }
            } catch (error) {
              console.warn("Error fitting bounds:", error);
            }
          }
        });
      } catch (error) {
        console.error("Error rendering geometry on preview map:", error);
      }
    }

    return () => {
      // Cleanup will be handled in the main cleanup effect
    };
  }, [geometry]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
      <div ref={mapRef} style={{ height }} className="w-full" />
      {!geometry && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-2">🗺️</div>
            <div className="text-sm">No geometry selected</div>
          </div>
        </div>
      )}
    </div>
  );
};
